import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AvatarEditor } from "../components/AvatarEditor";
import { useLocalProfile } from "../local/useLocalProfile";
import { createProfile } from "../local/profile";
import { createPlayerOnServer } from "../local/playerAccount";
import type { AvatarConfig } from "@shared/types";

export function LocalCreate() {
  const nav = useNavigate();
  const { profile, setProfile } = useLocalProfile();
  const [name, setName] = useState(profile?.displayName ?? "");
  const [avatar, setAvatar] = useState<AvatarConfig>(profile?.avatarConfig ?? {});
  const [language, setLanguage] = useState<"en" | "vi">(profile?.learnLanguage ?? "en");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [issuedCode, setIssuedCode] = useState<string | null>(null);

  const start = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setErr("Pick a name");
      return;
    }
    const fresh = createProfile(trimmed, avatar, language);
    setBusy(true);
    setErr(null);
    try {
      const { code } = await createPlayerOnServer(fresh);
      const withCode = { ...fresh, code };
      setProfile(withCode);
      setIssuedCode(code);
    } catch (e) {
      // Fall back to offline-only play so we don't block the kid.
      setProfile(fresh);
      setErr("Couldn't reach server — playing offline. Progress won't sync.");
      setTimeout(() => nav("/", { replace: true }), 1200);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="home-dashboard create-page">
      <div className="create-panel stack">
      <h1>
        {profile ? "Make a new character" : "Make your character"}
      </h1>
        <input
          className="input"
          autoFocus
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={20}
          style={{ textAlign: "center" }}
        />
        <AvatarEditor initial={avatar} controlledOnly onChange={setAvatar} />

        <div style={{ display: "grid", gap: 6 }}>
          <div className="t-muted" style={{ fontWeight: 700, fontSize: "0.9rem" }}>
            I want to learn
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className={language === "en" ? "btn btn-primary btn-block" : "btn btn-ghost btn-block"}
              onClick={() => setLanguage("en")}
            >
              🇬🇧 English
            </button>
            <button
              type="button"
              className={language === "vi" ? "btn btn-primary btn-block" : "btn btn-ghost btn-block"}
              onClick={() => setLanguage("vi")}
            >
              🇻🇳 Tiếng Việt
            </button>
          </div>
        </div>

        {err && <div className="error t-center">{err}</div>}
        <button className="btn btn-primary btn-big btn-block" onClick={start} disabled={busy}>
          {busy ? "Saving…" : "Start playing →"}
        </button>
        {profile && (
          <button className="btn btn-ghost btn-block" onClick={() => nav("/")}>
            ← Back without saving
          </button>
        )}
      </div>
      {issuedCode && (
        <div className="home-mode-overlay" role="dialog" aria-modal="true" onClick={() => nav("/", { replace: true })}>
          <div className="home-mode-card t-center" style={{ maxWidth: 380, position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <button className="popup-close-x" onClick={() => nav("/", { replace: true })} aria-label="Close">×</button>
            <h2>Your player code</h2>
            <p className="t-muted" style={{ marginTop: 4 }}>
              Save this 4-digit code — you can log in from any device with it.
            </p>
            <div
              style={{
                fontSize: "3.4rem",
                fontWeight: 900,
                letterSpacing: "0.4rem",
                margin: "18px 0 22px",
                color: "#fff",
                textShadow: "0 4px 18px rgba(0,0,0,0.45)",
                background: "linear-gradient(180deg, #ffd166, #f59f00)",
                padding: "10px 0",
                borderRadius: 18,
              }}
            >
              {issuedCode}
            </div>
            <button className="btn btn-primary btn-big btn-block" onClick={() => nav("/", { replace: true })}>
              Got it — let's play!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
